import type { ReactNode } from "react";
import { useAuth } from "@clerk/react";
import { Navigate, useLocation } from "react-router-dom";
import { useUser } from "../contexts/UserContext";
import { AppLoader } from "../components/ui/AppLoader";
import { getPartnerApp } from "../utils/partnerApp";

interface ProtectedRouteProps {
  children: ReactNode;
  requireAuth?: boolean;
  allowUnauthenticated?: boolean;
  allowedRoles?: ("doctor" | "partner" | "insurance" | "laboratory")[];
}

const portalRoutes = {
  hospital: {
    onboarding: "/onboarding/hospital-clinic",
    dashboard: "/dashboard",
    roles: ["doctor", "partner"],
  },
  laboratory: {
    onboarding: "/onboarding/laboratory",
    dashboard: "/lab/dashboard",
    roles: ["laboratory"],
  },
  insurance: {
    onboarding: "/onboarding/insurance",
    dashboard: "/insurance/dashboard",
    roles: ["insurance"],
  },
} satisfies Record<string, {
  onboarding: string;
  dashboard: string;
  roles: string[];
}>;

export function ProtectedRoute({
  children,
  requireAuth = true,
  allowUnauthenticated = false,
  allowedRoles,
}: ProtectedRouteProps) {
  const {
    isAuthenticated,
    hasCompletedOnboarding,
    user,
    loading,
  } = useUser();

  const clerkAuth = useAuth();
  const location = useLocation();
  const portal = portalRoutes[getPartnerApp()];
  const pathname = location.pathname.replace(/\/+$/, "") || "/";
  const isAuthPage = pathname === "/login" || pathname === "/signup";
  const isOnboardingPage =
    pathname === "/onboarding" ||
    pathname.startsWith("/onboarding/");
  const isPublic = allowUnauthenticated || !requireAuth;

  // Clerk identity permits onboarding; business access remains separately guarded.
  if (isOnboardingPage) {
    if (!clerkAuth.isLoaded || loading) return <AppLoader />;

    if (!clerkAuth.isSignedIn) {
      return <Navigate to="/login" state={{ from: location }} replace />;
    }

    if (!isAuthenticated || !hasCompletedOnboarding) {
      if (pathname !== portal.onboarding) {
        return <Navigate to={portal.onboarding} replace />;
      }

      return <div key={clerkAuth.userId}>{children}</div>;
    }
  }

  // Public landing and information pages remain accessible.
  if (isPublic && !isAuthPage) return <>{children}</>;
  if (loading) return <AppLoader />;

  if (!isAuthenticated) {
    if (isPublic) return <>{children}</>;
    return (
      <Navigate
        to="/login"
        state={{ from: location }}
        replace
      />
    );
  }

  // A completed account must have a role for this portal.
  if (
    hasCompletedOnboarding &&
    (!user?.role || !portal.roles.includes(user.role))
  ) {
    return <Navigate to="/unauthorized" replace />;
  }

  if (isAuthPage) {
    return (
      <Navigate
        to={hasCompletedOnboarding
          ? portal.dashboard
          : portal.onboarding}
        replace
      />
    );
  }

  if (isOnboardingPage) {
    if (hasCompletedOnboarding) {
      return <Navigate to={portal.dashboard} replace />;
    }

    if (pathname !== portal.onboarding) {
      return <Navigate to={portal.onboarding} replace />;
    }

    return <>{children}</>;
  }

  if (!hasCompletedOnboarding) {
    return <Navigate to={portal.onboarding} replace />;
  }

  const isOtherPortalRoute =
    (pathname === "/lab" || pathname.startsWith("/lab/"))
      ? getPartnerApp() !== "laboratory"
      : (pathname === "/insurance" || pathname.startsWith("/insurance/"))
        ? getPartnerApp() !== "insurance"
        : false;

  if (
    isOtherPortalRoute ||
    (allowedRoles &&
      (!user?.role || !allowedRoles.includes(user.role)))
  ) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
}

export default ProtectedRoute;
